import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

/**
 * Realistic architectural building (single procedural Three.js model).
 *
 * Style: modern slim curtain-wall skyscraper with mechanical crown.
 * Performance: InstancedMesh for floor plates and vertical mullions,
 * low DPR cap, PMREM environment generated once, no shadow maps.
 */
export default function Building3D() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    // ---------- Renderer ----------
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    mount.appendChild(renderer.domElement);

    // ---------- Scene & Camera ----------
    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x141728, 30, 90);

    const camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 200);
    camera.position.set(18, 14, 22);
    camera.lookAt(0, 6, 0);

    // ---------- Environment ----------
    const pmrem = new THREE.PMREMGenerator(renderer);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = env;

    // ---------- Lights ----------
    scene.add(new THREE.AmbientLight(0xffffff, 0.25));

    const sun = new THREE.DirectionalLight(0xfff1d6, 1.6);
    sun.position.set(14, 20, 10);
    scene.add(sun);

    const sky = new THREE.HemisphereLight(0xb6c8ff, 0x202337, 0.45);
    scene.add(sky);

    const coral = new THREE.PointLight(0xee5754, 1.3, 30);
    coral.position.set(-8, 6, -3);
    scene.add(coral);

    // ---------- Materials ----------
    // Glass curtain wall (cheap glass: physical material WITHOUT transmission)
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0x1a2438,
      roughness: 0.08,
      metalness: 0.55,
      clearcoat: 1.0,
      clearcoatRoughness: 0.06,
      envMapIntensity: 1.4,
      reflectivity: 0.9,
    });

    const glassMatTop = new THREE.MeshPhysicalMaterial({
      color: 0x223558,
      roughness: 0.08,
      metalness: 0.55,
      clearcoat: 1.0,
      clearcoatRoughness: 0.06,
      envMapIntensity: 1.5,
    });

    // Aluminum mullions / fins
    const aluMat = new THREE.MeshStandardMaterial({
      color: 0xc8ccd4,
      roughness: 0.35,
      metalness: 0.85,
    });

    // Concrete podium
    const concreteMat = new THREE.MeshStandardMaterial({
      color: 0x9a948a,
      roughness: 0.85,
      metalness: 0.05,
    });

    const concreteDarkMat = new THREE.MeshStandardMaterial({
      color: 0x4f5060,
      roughness: 0.7,
      metalness: 0.15,
    });

    const coralMat = new THREE.MeshStandardMaterial({
      color: 0xee5754,
      emissive: 0xee5754,
      emissiveIntensity: 0.55,
      roughness: 0.4,
      metalness: 0.4,
    });

    // ---------- Helpers ----------
    const root = new THREE.Group();
    root.position.set(0, -7, 0);
    scene.add(root);

    // Reflective dark plaza
    const plaza = new THREE.Mesh(
      new THREE.CircleGeometry(28, 64),
      new THREE.MeshStandardMaterial({
        color: 0x10131f,
        roughness: 0.25,
        metalness: 0.85,
        envMapIntensity: 0.7,
      })
    );
    plaza.rotation.x = -Math.PI / 2;
    root.add(plaza);

    // Subtle plaza pattern (concentric rings)
    for (let i = 1; i <= 4; i++) {
      const r = i * 5.5;
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(r - 0.02, r + 0.02, 64),
        new THREE.MeshBasicMaterial({ color: 0x2a2d45, transparent: true, opacity: 0.4 })
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.01;
      root.add(ring);
    }

    // ---------- Main Tower ----------
    // Tower main body: 4w x 24h x 4d, 24 floors of 1m each
    const TOWER_W = 4.2;
    const TOWER_D = 4.2;
    const FLOOR_H = 1.0;
    const FLOORS = 24;
    const TOWER_H = FLOORS * FLOOR_H;
    const TOWER_BASE_Y = 1.8; // sits on podium

    const towerGroup = new THREE.Group();
    towerGroup.position.set(0, TOWER_BASE_Y, 0);
    root.add(towerGroup);

    // Glass body (slightly inset behind mullions)
    const bodyGlass = new THREE.Mesh(
      new THREE.BoxGeometry(TOWER_W * 0.96, TOWER_H, TOWER_D * 0.96),
      glassMat
    );
    bodyGlass.position.y = TOWER_H / 2;
    towerGroup.add(bodyGlass);

    // Horizontal floor plates (visible through glass)
    const floorPlateGeo = new THREE.BoxGeometry(TOWER_W * 1.005, 0.06, TOWER_D * 1.005);
    const floorPlateMat = new THREE.MeshStandardMaterial({
      color: 0xe8e8eb,
      roughness: 0.6,
      metalness: 0.2,
    });
    const floorPlates = new THREE.InstancedMesh(floorPlateGeo, floorPlateMat, FLOORS + 1);
    const tmp = new THREE.Object3D();
    for (let f = 0; f <= FLOORS; f++) {
      tmp.position.set(0, f * FLOOR_H, 0);
      tmp.updateMatrix();
      floorPlates.setMatrixAt(f, tmp.matrix);
    }
    floorPlates.instanceMatrix.needsUpdate = true;
    towerGroup.add(floorPlates);

    // Floor interior light strip — instanced thin emissive
    const stripGeo = new THREE.BoxGeometry(TOWER_W * 0.92, 0.04, TOWER_D * 0.92);
    const stripMat = new THREE.MeshStandardMaterial({
      color: 0xffe1aa,
      emissive: 0xffe1aa,
      emissiveIntensity: 1.6,
    });
    const strips = new THREE.InstancedMesh(stripGeo, stripMat, FLOORS);
    for (let f = 0; f < FLOORS; f++) {
      // randomize lit/dark so building feels alive
      const lit = Math.random() > 0.45;
      tmp.position.set(0, f * FLOOR_H + 0.5, 0);
      tmp.scale.set(lit ? 1 : 0.001, lit ? 1 : 0.001, lit ? 1 : 0.001);
      tmp.updateMatrix();
      strips.setMatrixAt(f, tmp.matrix);
    }
    strips.instanceMatrix.needsUpdate = true;
    tmp.scale.set(1, 1, 1);
    towerGroup.add(strips);

    // Vertical mullions (fins) - thin aluminum stripes
    const MULLION_COUNT_PER_SIDE = 9;
    const mullionGeo = new THREE.BoxGeometry(0.04, TOWER_H, 0.18);
    const mullions = new THREE.InstancedMesh(
      mullionGeo,
      aluMat,
      MULLION_COUNT_PER_SIDE * 2 + (MULLION_COUNT_PER_SIDE) * 2
    );
    let mIdx = 0;
    // Front & Back faces
    for (let i = 0; i < MULLION_COUNT_PER_SIDE; i++) {
      const x = -TOWER_W / 2 + (i / (MULLION_COUNT_PER_SIDE - 1)) * TOWER_W;
      // front
      tmp.position.set(x, TOWER_H / 2, TOWER_D / 2 + 0.02);
      tmp.rotation.set(0, 0, 0);
      tmp.updateMatrix();
      mullions.setMatrixAt(mIdx++, tmp.matrix);
      // back
      tmp.position.set(x, TOWER_H / 2, -TOWER_D / 2 - 0.02);
      tmp.updateMatrix();
      mullions.setMatrixAt(mIdx++, tmp.matrix);
    }
    // Left & Right faces (rotate 90)
    for (let i = 0; i < MULLION_COUNT_PER_SIDE; i++) {
      const z = -TOWER_D / 2 + (i / (MULLION_COUNT_PER_SIDE - 1)) * TOWER_D;
      // right
      tmp.position.set(TOWER_W / 2 + 0.02, TOWER_H / 2, z);
      tmp.rotation.set(0, Math.PI / 2, 0);
      tmp.updateMatrix();
      mullions.setMatrixAt(mIdx++, tmp.matrix);
      // left
      tmp.position.set(-TOWER_W / 2 - 0.02, TOWER_H / 2, z);
      tmp.updateMatrix();
      mullions.setMatrixAt(mIdx++, tmp.matrix);
    }
    mullions.instanceMatrix.needsUpdate = true;
    tmp.rotation.set(0, 0, 0);
    towerGroup.add(mullions);

    // Horizontal floor band trim (one per 4 floors as architectural feature)
    const bandGeo = new THREE.BoxGeometry(TOWER_W + 0.08, 0.12, TOWER_D + 0.08);
    const bandCount = Math.floor(FLOORS / 4);
    const bands = new THREE.InstancedMesh(bandGeo, aluMat, bandCount);
    for (let i = 0; i < bandCount; i++) {
      tmp.position.set(0, (i + 1) * 4 * FLOOR_H, 0);
      tmp.updateMatrix();
      bands.setMatrixAt(i, tmp.matrix);
    }
    bands.instanceMatrix.needsUpdate = true;
    towerGroup.add(bands);

    // ---------- Mechanical Crown ----------
    const crown = new THREE.Group();
    crown.position.y = TOWER_H + 0.05;
    towerGroup.add(crown);

    // Setback level 1
    const setback1 = new THREE.Mesh(
      new THREE.BoxGeometry(TOWER_W * 0.85, 1.6, TOWER_D * 0.85),
      glassMatTop
    );
    setback1.position.y = 0.8;
    crown.add(setback1);

    // Mechanical floor (concrete with vents)
    const mech = new THREE.Mesh(
      new THREE.BoxGeometry(TOWER_W * 0.85 + 0.06, 1.0, TOWER_D * 0.85 + 0.06),
      concreteDarkMat
    );
    mech.position.y = 2.2;
    crown.add(mech);

    // Vent slats (instanced)
    const ventGeo = new THREE.BoxGeometry(TOWER_W * 0.78, 0.06, 0.04);
    const vents = new THREE.InstancedMesh(ventGeo, aluMat, 8);
    for (let i = 0; i < 8; i++) {
      tmp.position.set(0, 1.85 + i * 0.1, TOWER_D * 0.42 + 0.005);
      tmp.updateMatrix();
      vents.setMatrixAt(i, tmp.matrix);
    }
    vents.instanceMatrix.needsUpdate = true;
    crown.add(vents);

    // Top setback
    const setback2 = new THREE.Mesh(
      new THREE.BoxGeometry(TOWER_W * 0.55, 1.4, TOWER_D * 0.55),
      glassMatTop
    );
    setback2.position.y = 3.4;
    crown.add(setback2);

    // Helipad cap
    const helipad = new THREE.Mesh(
      new THREE.CylinderGeometry(TOWER_W * 0.32, TOWER_W * 0.34, 0.2, 24),
      concreteMat
    );
    helipad.position.y = 4.2;
    crown.add(helipad);

    // Helipad H marking (using a small thin emissive plane)
    const heliMark = new THREE.Mesh(
      new THREE.CircleGeometry(TOWER_W * 0.28, 24),
      new THREE.MeshStandardMaterial({ color: 0xee5754, emissive: 0xee5754, emissiveIntensity: 0.3 })
    );
    heliMark.rotation.x = -Math.PI / 2;
    heliMark.position.y = 4.31;
    crown.add(heliMark);

    // Antenna (telescoping)
    const ant1 = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.09, 1.6, 12),
      aluMat
    );
    ant1.position.y = 5.1;
    crown.add(ant1);
    const ant2 = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.07, 1.4, 12),
      aluMat
    );
    ant2.position.y = 6.3;
    crown.add(ant2);
    const tipBeacon = new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 12, 12),
      coralMat
    );
    tipBeacon.position.y = 7.05;
    crown.add(tipBeacon);

    // ---------- Podium / Ground Floors ----------
    // 1st floor podium (taller darker base)
    const podium = new THREE.Mesh(
      new THREE.BoxGeometry(TOWER_W * 1.4, 1.8, TOWER_D * 1.4),
      concreteDarkMat
    );
    podium.position.y = 0.9;
    root.add(podium);

    // Lobby glass (bright, lit interior)
    const lobby = new THREE.Mesh(
      new THREE.BoxGeometry(TOWER_W * 1.42, 1.4, TOWER_D * 1.42),
      new THREE.MeshStandardMaterial({
        color: 0xffe1aa,
        emissive: 0xffe1aa,
        emissiveIntensity: 0.9,
        transparent: true,
        opacity: 0.85,
      })
    );
    lobby.position.y = 0.9;
    root.add(lobby);

    // Podium top cap
    const podCap = new THREE.Mesh(
      new THREE.BoxGeometry(TOWER_W * 1.45, 0.06, TOWER_D * 1.45),
      concreteMat
    );
    podCap.position.y = 1.83;
    root.add(podCap);

    // Plaza steps (3 tiers around podium)
    for (let i = 0; i < 3; i++) {
      const w = TOWER_W * 1.6 + i * 0.6;
      const step = new THREE.Mesh(
        new THREE.BoxGeometry(w, 0.12, w),
        concreteMat
      );
      step.position.y = -i * 0.12 - 0.01;
      root.add(step);
    }

    // ---------- Surrounding context (low-rise neighbours) ----------
    function makeContextBuilding(w, h, d, x, z, glassColor = 0x2a3045) {
      const g = new THREE.Group();
      g.position.set(x, 0, z);

      const cMat = new THREE.MeshPhysicalMaterial({
        color: glassColor,
        roughness: 0.18,
        metalness: 0.5,
        clearcoat: 0.8,
      });
      const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), cMat);
      body.position.y = h / 2;
      g.add(body);

      // floor lines (every 1 unit)
      const lines = Math.floor(h);
      const lineGeo = new THREE.BoxGeometry(w + 0.01, 0.04, d + 0.01);
      const lineMat = new THREE.MeshStandardMaterial({ color: 0xb5b9c2, roughness: 0.4 });
      const inst = new THREE.InstancedMesh(lineGeo, lineMat, lines);
      const t = new THREE.Object3D();
      for (let i = 1; i <= lines; i++) {
        t.position.set(0, i, 0);
        t.updateMatrix();
        inst.setMatrixAt(i - 1, t.matrix);
      }
      inst.instanceMatrix.needsUpdate = true;
      g.add(inst);

      // roof cap
      const cap = new THREE.Mesh(
        new THREE.BoxGeometry(w * 1.04, 0.2, d * 1.04),
        concreteDarkMat
      );
      cap.position.y = h + 0.1;
      g.add(cap);

      // little rooftop unit
      const ru = new THREE.Mesh(
        new THREE.BoxGeometry(w * 0.4, 0.5, d * 0.4),
        concreteMat
      );
      ru.position.set((Math.random() - 0.5) * w * 0.4, h + 0.45, (Math.random() - 0.5) * d * 0.4);
      g.add(ru);

      root.add(g);
    }

    makeContextBuilding(3.0, 8, 3.0, -10, -2, 0x2c3550);
    makeContextBuilding(2.6, 6, 2.6, -12, 5, 0x39405c);
    makeContextBuilding(3.4, 10, 3.4, 9, 4, 0x2a3045);
    makeContextBuilding(2.4, 5, 2.4, 11, -3, 0x33394f);
    makeContextBuilding(2.0, 4, 2.0, -7, 8, 0x2a2f44);
    makeContextBuilding(2.2, 4.5, 2.2, 7, -8, 0x2c3550);

    // ---------- Floating coral logo cube (brand mark) ----------
    const fGeo = new THREE.BoxGeometry(0.55, 0.55, 0.55);
    const floater = new THREE.Mesh(
      fGeo,
      new THREE.MeshStandardMaterial({
        color: 0xee5754,
        emissive: 0xee5754,
        emissiveIntensity: 0.7,
        roughness: 0.25,
        metalness: 0.45,
      })
    );
    floater.position.set(7, 14, 5);
    root.add(floater);
    const floaterEdges = new THREE.LineSegments(
      new THREE.EdgesGeometry(fGeo),
      new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.7 })
    );
    floater.add(floaterEdges);

    // ---------- Mouse interaction ----------
    let targetRotY = 0;
    let targetRotX = 0;
    const onMove = (e) => {
      const rect = mount.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      targetRotY = x * 0.4;
      targetRotX = y * 0.12;
    };
    mount.addEventListener('mousemove', onMove);

    // ---------- Resize ----------
    const onResize = () => {
      width = mount.clientWidth;
      height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    // ---------- Animation Loop ----------
    let frame;
    let t = 0;
    const clock = new THREE.Clock();

    // Throttle — skip frames if tab not visible
    let visible = true;
    const onVis = () => { visible = !document.hidden; };
    document.addEventListener('visibilitychange', onVis);

    const animate = () => {
      const dt = Math.min(clock.getDelta(), 0.05);
      t += dt;

      // smooth orbit
      root.rotation.y += dt * 0.08;
      root.rotation.y += (targetRotY * 0.4 - (root.rotation.y - root.rotation.y)) * 0.0;
      root.rotation.x += (targetRotX - root.rotation.x) * 0.04;

      // floater pulse
      floater.position.y = 14 + Math.sin(t * 1.2) * 0.4;
      floater.rotation.y = t * 0.7;
      floater.rotation.x = t * 0.3;

      // beacon blink
      tipBeacon.material.emissiveIntensity = 0.4 + (Math.sin(t * 4) * 0.5 + 0.5) * 1.0;

      // camera bob
      camera.position.x = 18 + Math.sin(t * 0.15) * 0.6;
      camera.position.y = 14 + targetRotX * 1.5;
      camera.lookAt(0, 6, 0);

      if (visible) renderer.render(scene, camera);
      frame = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      mount.removeEventListener('mousemove', onMove);
      document.removeEventListener('visibilitychange', onVis);
      renderer.dispose();
      pmrem.dispose();
      if (renderer.domElement && mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose?.();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose?.());
          else obj.material.dispose?.();
        }
      });
    };
  }, []);

  return <div ref={mountRef} className="w-full h-full" />;
}
